/**
 * Quy đổi credit → "khả năng tạo" cho từng gói (UI sales view).
 *
 * Mục tiêu: thay vì hiển thị "350 Credit" trừu tượng, ta nói cho user biết
 * 350 credit ≈ bao nhiêu giáo án / slide / đề kiểm tra mỗi tháng.
 *
 * Công thức:
 *   itemsPerMonth = credits / (baseCost × tierMultiplier × safetyBuffer)
 *
 *   - baseCost: chi phí gốc của tính năng, đồng bộ với CreditCost ở config.ts
 *     và CreditCostSettings.php ở backend.
 *   - tierMultiplier: hệ số nhân theo cấp model AI (basic=1, premium=1.5,
 *     advanced=2.0). Ước tính trung bình của cặp provider/model phổ biến
 *     trong credit_pricing.php.
 *   - safetyBuffer (1.05): trừ ~5% buffer để con số "khả năng tạo" hiển thị
 *     không bị "thiếu hụt" so với thực tế khi user chạy nhiều task nặng.
 *     Bán hàng nguyên tắc UNDER-PROMISE / OVER-DELIVER.
 *
 * Số trả về luôn LÀM TRÒN XUỐNG bội số 5 hoặc 10 (làm tròn cảm giác đẹp,
 * tránh "73 giáo án" gây lăn tăn — chuyển thành "~70 giáo án").
 */
import { UI_CONFIG } from '../config';

export type AiTier = 'basic' | 'premium' | 'advanced';

/**
 * Hệ số nhân multiplier trung bình theo cấp AI tier — tính từ credit_pricing.php.
 *  - basic    = gemini-2.5-flash / gpt-4o-mini (multiplier 1.0)
 *  - premium  = gemini-2.5-pro / gpt-4o (avg ~1.75, làm tròn 1.5 cho "ngon")
 *  - advanced = gpt-5.5 / claude-opus-4 (avg ~3.0, dùng 2.0 vì backend route
 *               đa phần vẫn dùng gemini-pro để tiết kiệm)
 */
const TIER_MULTIPLIER: Record<AiTier, number> = {
  basic: 1.0,
  premium: 1.5,
  advanced: 2.0,
};

/**
 * Chi phí gốc đồng bộ với CreditCost trong config.ts và CreditCostSettings.php.
 * Nếu admin chỉnh setting → ta vẫn ước tính theo bảng cố định này (UI only).
 */
const BASE_COST = {
  lessonPlan: 2,
  presentation: 3,
  quiz: 1,
  homework: 1,
  game: 1,
  childPlan: 1,
  image: 2,
  video: 5,
  html5: 6,
  ttsMin: 1,
} as const;

const SAFETY_BUFFER = 1.05;

export type UserPersona = 'teacher' | 'parent' | 'student';

export interface CreditCapacity {
  /** ≈ Số giáo án AI có thể tạo trong tháng */
  lessonPlans: number;
  /** ≈ Số slide bài giảng (3-5 trang) */
  presentations: number;
  /** ≈ Số đề kiểm tra / câu hỏi nhanh */
  quizzes: number;
  /** ≈ Số bài tập về nhà / phiếu bài tập */
  homeworks: number;
  /** ≈ Số lộ trình học tập (con / bản thân) */
  childPlans: number;
  /** ≈ Số trò chơi giáo dục */
  games: number;
  /** ≈ Số hình minh hoạ AI */
  images: number;
  /** ≈ Số video AI ngắn */
  videos: number;
}

export interface PersonaCapacityLine {
  count: number;
  label: string;
}

/** 3 chỉ số đại diện theo vai trò — đồng bộ menu & luồng tính năng thực tế. */
const PERSONA_HEADLINES: Record<UserPersona, Array<{ key: keyof CreditCapacity; label: string }>> = {
  teacher: [
    { key: 'lessonPlans', label: 'giáo án' },
    { key: 'presentations', label: 'slide bài giảng' },
    { key: 'quizzes', label: 'đề kiểm tra' },
  ],
  parent: [
    { key: 'childPlans', label: 'lộ trình học tập cho con' },
    { key: 'quizzes', label: 'đề luyện cho con' },
    { key: 'homeworks', label: 'phiếu bài tập' },
  ],
  student: [
    { key: 'childPlans', label: 'lộ trình học tập' },
    { key: 'quizzes', label: 'đề luyện' },
    { key: 'games', label: 'trò chơi giáo dục' },
  ],
};

/**
 * Tính khả năng tạo gần đúng cho 1 chu kỳ (tháng nếu monthly, năm nếu annually).
 * Trả về object chứa số giáo án/slide/đề... ước lượng.
 */
export function computeCapacity(credits: number, tier: AiTier = 'premium'): CreditCapacity {
  const m = TIER_MULTIPLIER[tier] ?? 1;
  const calc = (baseCost: number) => {
    if (credits <= 0) return 0;
    const eff = baseCost * m * SAFETY_BUFFER;
    const raw = credits / eff;
    return roundDownPretty(raw);
  };
  return {
    lessonPlans: calc(BASE_COST.lessonPlan),
    presentations: calc(BASE_COST.presentation),
    quizzes: calc(BASE_COST.quiz),
    homeworks: calc(BASE_COST.homework),
    childPlans: calc(BASE_COST.childPlan),
    games: calc(BASE_COST.game),
    images: calc(BASE_COST.image),
    videos: calc(BASE_COST.video),
  };
}

/**
 * Ba chỉ số ước tính phù hợp vai trò (giáo viên / phụ huynh / học sinh).
 */
export function getPersonaCapacityEstimate(
  credits: number,
  persona: UserPersona = 'teacher',
  tier: AiTier = 'premium',
): PersonaCapacityLine[] {
  const cap = computeCapacity(credits, tier);
  return PERSONA_HEADLINES[persona].map(({ key, label }) => ({
    count: cap[key],
    label,
  }));
}

/**
 * Tooltip "Khả năng AI ước tính" trên chip credit — theo đúng loại user.
 */
export function formatCapacityEstimateTooltip(
  credits: number,
  persona: UserPersona = 'teacher',
  tier: AiTier = 'premium',
): string | undefined {
  if (credits <= 0) return undefined;
  const lines = getPersonaCapacityEstimate(credits, persona, tier).filter(l => l.count > 0);
  if (lines.length === 0) return undefined;
  const body = lines.map(l => `• ~${l.count} ${l.label}`).join('\n');
  return `Khả năng AI ước tính:\n${body}`;
}

/** Chuẩn hoá role từ UserSettings / activeRoleConfig → UserPersona. */
export function resolveUserPersona(role: string | null | undefined): UserPersona {
  if (role === 'parent') return 'parent';
  if (role === 'student') return 'student';
  return 'teacher';
}

/**
 * Lấy 3 chỉ số "đại diện" để hiển thị trong card gói (không gây overload).
 * Ưu tiên: giáo án (anchor cảm xúc cao nhất với GV), slide, đề kiểm tra.
 */
export function getHeadlineCapacity(
  credits: number,
  billingCycle: 'monthly' | 'annually' | 'onetime' | 'contact' | string,
  tier: AiTier = 'premium',
): { lessonPlans: number; presentations: number; quizzes: number; perPeriod: string } {
  // Quy về "mỗi tháng" cho subscription để con số dễ hình dung.
  // Onetime: hiển thị tổng (không chia tháng).
  let monthlyCredits = credits;
  let perPeriod = 'mỗi tháng';
  if (billingCycle === 'annually') {
    monthlyCredits = Math.floor(credits / 12);
  } else if (billingCycle === 'onetime') {
    perPeriod = 'sử dụng linh hoạt';
  } else if (billingCycle === 'contact') {
    perPeriod = 'theo nhu cầu';
  }
  const cap = computeCapacity(monthlyCredits, tier);
  return {
    lessonPlans: cap.lessonPlans,
    presentations: cap.presentations,
    quizzes: cap.quizzes,
    perPeriod,
  };
}

/**
 * Format text "Phù hợp cho: ..." dựa trên đặc tính gói.
 * Pattern này tăng conversion rất mạnh — user tự nhận diện chính mình.
 */
export interface PackageHints {
  credits: number;
  billingCycle: string;
  aiTier?: AiTier;
  canShareCredits?: boolean;
  canAllocateGroupMonthly?: boolean;
  maxGroupMembers?: number;
  maxChildren?: number;
  isPopular?: boolean;
  priceVND: number;
}

export function getPersonaFit(pkg: PackageHints): string {
  if (pkg.priceVND === -1) {
    return 'Trường học, hệ thống nhiều cơ sở';
  }
  if (pkg.canShareCredits || pkg.canAllocateGroupMonthly) {
    if ((pkg.maxGroupMembers ?? 0) >= 25) {
      return 'Doanh nghiệp & trung tâm 25+ giáo viên';
    }
    return 'Tổ chuyên môn & nhóm 10+ giáo viên';
  }
  if (pkg.billingCycle === 'onetime') {
    if (pkg.credits >= 5000) return 'Power user / GV chạy AI khối lượng lớn';
    if (pkg.credits >= 1000) return 'Khi cần "nạp thêm" cho mùa cao điểm';
    return 'Bổ sung nhanh khi gần hết';
  }
  if (pkg.aiTier === 'advanced') {
    return 'Trung tâm nhỏ / Giáo viên AI-heavy chuyên sâu';
  }
  if (pkg.aiTier === 'premium') {
    return pkg.isPopular
      ? 'Giáo viên dùng AI hằng ngày (lựa chọn phổ biến nhất)'
      : 'Giáo viên dùng AI thường xuyên';
  }
  if (pkg.credits === 0) {
    return 'Trải nghiệm BookAI lần đầu';
  }
  return 'Giáo viên dùng nhẹ, lớp nhỏ';
}

/**
 * Mô tả AI tier theo NGÔN NGỮ KẾT QUẢ thay vì tên model kỹ thuật.
 * (Đề xuất #3 trong bộ pricing UX overhaul)
 */
/**
 * Nguồn sự thật DUY NHẤT cho "dòng tiêu đề số dư" của người dùng — số credit thô
 * hay tên cấp gói, tuỳ theo pricing_display_mode (Admin → Cài đặt → AI).
 * Mọi nơi hiển thị số dư credit ở góc trên (Header desktop, menu avatar mobile,
 * sidebar Deep Workspace...) PHẢI gọi hàm này thay vì tự viết lại điều kiện
 * isPlanMode — tránh tình trạng mỗi nơi tự quyết định một kiểu khác nhau.
 */
export function formatCreditBalanceHeadline(credits: number, tier?: AiTier | string): string {
  if (UI_CONFIG.PRICING_DISPLAY_MODE === 'plan') {
    return getAiTierLabel(tier).short;
  }
  const label = UI_CONFIG.CREDIT_LABEL || 'Tín dụng';
  return `${(credits || 0).toLocaleString('vi-VN')} ${label}`;
}

export function getAiTierLabel(tier?: AiTier | string): { short: string; full: string; tone: string } {
  if (tier === 'advanced') {
    return {
      short: 'AI Cao cấp Pro',
      full: 'AI Cao cấp Pro — Sâu nhất, ưu tiên hàng đợi xử lý',
      tone: 'indigo',
    };
  }
  if (tier === 'premium') {
    return {
      short: 'AI Cao cấp',
      full: 'AI Cao cấp — Sâu & Chất lượng cao cho nội dung chuyên sâu',
      tone: 'teal',
    };
  }
  return {
    short: 'AI Tiêu chuẩn',
    full: 'AI Tiêu chuẩn — Nhanh & Tiết kiệm cho công việc hằng ngày',
    tone: 'slate',
  };
}

/**
 * Hạn mức Chat AI mềm theo tier (Soft Unlimited model).
 * Đồng bộ với backend rate-limit (P4): Free 20/ngày · Pro 200 · Advanced 500 · Team 1000.
 *
 * Ưu tiên giá trị `chatMessagesPerDay` từ DB (nếu admin chỉnh khác mặc định)
 * → fallback về tier mặc định.
 */
export function getSoftChatLimit(opts: {
  isPaid: boolean;
  aiTier?: AiTier | string;
  isGroup?: boolean;
  /** Giá trị thực từ DB (chat_messages_per_day). Ưu tiên nếu > 0 hoặc = -1 (unlimited). */
  chatMessagesPerDay?: number;
}): { messagesPerDay: number; label: string } {
  const dbVal = opts.chatMessagesPerDay;
  if (dbVal === -1) return { messagesPerDay: -1, label: 'Chat AI không giới hạn' };
  if (typeof dbVal === 'number' && dbVal > 0) {
    if (dbVal >= 500) return { messagesPerDay: dbVal, label: 'Chat AI gần như không giới hạn' };
    if (dbVal >= 100) return { messagesPerDay: dbVal, label: 'Chat AI thoải mái mỗi ngày' };
    return { messagesPerDay: dbVal, label: `Chat AI ${dbVal} tin nhắn/ngày` };
  }
  if (opts.isGroup) return { messagesPerDay: 1000, label: 'Chat AI gần như không giới hạn' };
  if (!opts.isPaid) return { messagesPerDay: 20, label: 'Chat AI 20 tin nhắn/ngày (gói Free)' };
  if (opts.aiTier === 'advanced') return { messagesPerDay: 500, label: 'Chat AI gần như không giới hạn' };
  if (opts.aiTier === 'premium') return { messagesPerDay: 200, label: 'Chat AI thoải mái mỗi ngày' };
  return { messagesPerDay: 100, label: 'Chat AI thoải mái mỗi ngày' };
}

/**
 * Format số "đẹp" cho marketing — làm tròn xuống bội 5/10 và prefix "~".
 * 73 → "~70" · 234 → "~230" · 8 → "~5"
 */
function roundDownPretty(value: number): number {
  if (value <= 0) return 0;
  if (value < 1) return 0;
  if (value < 10) return Math.floor(value);
  if (value < 100) return Math.floor(value / 5) * 5;
  if (value < 1000) return Math.floor(value / 10) * 10;
  return Math.floor(value / 50) * 50;
}

/**
 * Format prefix "~" hoặc "≈" cho con số (UI helper).
 */
export function formatApprox(n: number): string {
  if (n <= 0) return '—';
  return `~${n.toLocaleString('vi-VN')}`;
}
